import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SearchIcon } from "lucide-react";
import {
  IProduct,
  IProductVariant,
  IWishlist,
  defaultPaginationOptions,
  IPaginatedResponse,
  SEARCH_DEBOUNCE_DELAY,
} from "muminsmart-utils";
import {
  apiSlice,
} from "@/lib/api/apiSlice";
import { useAppDispatch, useAppStore } from "@/lib/hooks";
import { cn } from "@/lib/utils";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { toast } from "sonner";
import ProductCard from "./ProductCard";
import ProductListingSkeleton from "@/app/components/product/ProductListingSkeleton";
import ProductsComingSoon from "./ProductsComingSoon";

export interface ProductListingItem {
  key: string;
  variant: IProductVariant;
  product?: IProduct;
}

export interface ProductListingFetchParams {
  page: number;
  searchTerm: string;
}

type ProductListingFetchMode = "variant" | "wishlist";

export interface ProductListingFetchConfig {
  endpoint: string;
  query?: Record<string, unknown>;
  mode?: ProductListingFetchMode;
  errorMessage?: string;
}

interface ProductListingProps {
  items?: ProductListingItem[];
  fetchConfig?: ProductListingFetchConfig;
  className?: string;
  itemClassName?: string;
  emptyState?: React.ReactNode;
  showSearch?: boolean;
  searchPlaceholder?: string;
}

function toListingItems(
  data: IPaginatedResponse<IProductVariant | IWishlist>,
  mode: ProductListingFetchMode,
): ProductListingItem[] {
  if (mode === "wishlist") {
    return (data.items as IWishlist[]).flatMap((wishlistItem) =>
      wishlistItem.productVariant
        ? [
            {
              key: wishlistItem.productVariant.id,
              variant: wishlistItem.productVariant,
            },
          ]
        : [],
    );
  }

  return (data.items as IProductVariant[]).map((variant) => ({
    key: variant.id,
    variant,
  }));
}

const ProductListing = ({
  items,
  fetchConfig,
  className,
  itemClassName,
  emptyState = <ProductsComingSoon />,
  showSearch = false,
  searchPlaceholder = "Search...",
}: ProductListingProps) => {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [managedItems, setManagedItems] = useState<ProductListingItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(
    Boolean(fetchConfig),
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);

  const displayItems = fetchConfig ? managedItems : (items ?? []);

  const buildQueryArgs = useCallback(
    (nextPage: number) => ({
      endpoint: fetchConfig!.endpoint,
      params: {
        ...defaultPaginationOptions,
        ...fetchConfig!.query,
        page: nextPage,
        search: searchTerm,
      },
    }),
    [fetchConfig, searchTerm],
  );

  const applyPageData = useCallback(
    (
      data: IPaginatedResponse<IProductVariant | IWishlist>,
      nextPage: number,
    ) => {
      const mode = fetchConfig?.mode ?? "variant";
      const nextItems = toListingItems(data, mode);

      setManagedItems(nextItems);
      setPage(nextPage);
      setHasMore(data.meta.currentPage < data.meta.totalPages);
    },
    [fetchConfig?.mode],
  );

  const selectMergedPageData = useCallback(
    (nextPage: number) => {
      if (nextPage <= 1) {
        return null;
      }

      const cachedResult = apiSlice.endpoints.fetchPaginated.select(
        buildQueryArgs(1),
      )(store.getState());

      return cachedResult?.data as
        | IPaginatedResponse<IProductVariant | IWishlist>
        | undefined;
    },
    [buildQueryArgs, store],
  );

  const loadPage = useCallback(
    async (nextPage: number, reset = false) => {
      if (!fetchConfig || isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      const queryArgs = buildQueryArgs(nextPage);

      if (reset) {
        const cachedResult =
          apiSlice.endpoints.fetchPaginated.select(queryArgs)(
            store.getState(),
          );

        if (cachedResult?.data) {
          applyPageData(
            cachedResult.data as IPaginatedResponse<IProductVariant | IWishlist>,
            nextPage,
          );
          setIsInitialLoading(false);
          isFetchingRef.current = false;
          void dispatch(
            apiSlice.endpoints.fetchPaginated.initiate(queryArgs, {
              subscribe: false,
              forceRefetch: true,
            }),
          ).then((result) => {
            if ("data" in result && result.data) {
              applyPageData(
                result.data as IPaginatedResponse<IProductVariant | IWishlist>,
                nextPage,
              );
            }
          });
          return;
        }

        setIsInitialLoading(true);
        setManagedItems([]);
        setPage(1);
        setHasMore(false);
      } else {
        setIsLoadingMore(true);
      }

      try {
        const result = await dispatch(
          apiSlice.endpoints.fetchPaginated.initiate(queryArgs, {
            forceRefetch: reset,
          }),
        ).unwrap();

        const mergedData = selectMergedPageData(nextPage);
        applyPageData(
          (mergedData ??
            result) as IPaginatedResponse<IProductVariant | IWishlist>,
          nextPage,
        );
      } catch {
        toast.error(fetchConfig.errorMessage || "Error fetching products");
        if (reset) {
          setManagedItems([]);
          setHasMore(false);
        }
      } finally {
        isFetchingRef.current = false;
        if (reset) {
          setIsInitialLoading(false);
        } else {
          setIsLoadingMore(false);
        }
      }
    },
    [applyPageData, buildQueryArgs, dispatch, fetchConfig, selectMergedPageData, store],
  );

  useEffect(() => {
    if (!showSearch) {
      return;
    }

    const timeout = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchInput, showSearch]);

  useEffect(() => {
    if (!fetchConfig) {
      return;
    }

    void loadPage(1, true);
  }, [fetchConfig, loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !fetchConfig) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          void loadPage(page + 1);
        }
      },
      { threshold: 0.1, rootMargin: "200px" },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [fetchConfig, hasMore, isLoadingMore, loadPage, page]);

  let content: React.ReactNode;
  if (isInitialLoading) {
    content = (
      <ProductListingSkeleton
        className={className}
        itemCount={6}
      />
    );
  } else if (displayItems.length === 0) {
    content = emptyState;
  } else {
    content = (
      <div className={className}>
        {displayItems.map((item) => (
          <div
            key={item.key}
            className={cn(itemClassName, "[content-visibility:auto] [contain-intrinsic-size:0_280px]")}
          >
            <ProductCard
              product={item.product}
              variant={item.variant}
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {showSearch && (
        <div className="mb-4">
          <InputGroup className="w-full rounded-full border-none bg-white shadow-sm">
            <InputGroupInput
              type="text"
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
              }}
            />
            <InputGroupAddon>
              <SearchIcon className="h-4 w-4 text-gray-400" />
            </InputGroupAddon>
          </InputGroup>
        </div>
      )}

      {content}

      {isLoadingMore && !isInitialLoading ? (
        <ProductListingSkeleton className={className} itemCount={2} />
      ) : null}

      {fetchConfig && (
        <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
      )}
    </>
  );
};

export default ProductListing;
